/**
 * @file findCasesQuery.js
 * @description Shared query helpers for the case discovery view. Provides filter
 *   application, role-to-type normalization, page size constant, and the
 *   shouldShowLoadMore guard used by FindCasesV2.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/findCasesQuery
 */
// 200 fits any realistic single-area market density (current peak: ~50 cases
// in a 15mi radius) so the initial load covers the full area result set.
// Age/pay/setting/hours filtering all run client-side on this cached result —
// no DB hit per filter change. Load-more still works if a dense market exceeds 200.
export const PAGE_SIZE = 200;

/**
 * @description Resolves the case type filter based on user role and explicit filter selection.
 *   RBTs always see RBT cases; BCaBAs see BCABA; BCBAs and others respect the filter.
 * @param {string} userRole - Current user's role (rbt, bcba, bcaba, guest)
 * @param {string} [filterType="All"] - Selected filter type ("All", "RBT", "BCBA", "BCABA")
 * @returns {string|null} Resolved type string, or null for no filter
 */
export function normalizeRoleTypeForQuery(userRole, filterType = "All") {
  if (userRole === "rbt")   return ["RBT", "BT"];
  if (userRole === "bcaba") return "BCABA";
  // BCBAs default to BCBA cases but can browse other types — respect filterType
  // when explicitly set rather than always locking to BCBA.
  if (Array.isArray(filterType)) return filterType.length === 1 ? filterType[0] : null;
  if (filterType && filterType !== "All") return filterType;
  if (userRole === "bcba")  return "BCBA";  // default when no explicit filter
  return null;
}

/**
 * Case types an SEO city page is about, for the "are cases open near X?" check (public_open_case_count p_types):
 * an RBT page counts what an RBT sees (normalizeRoleTypeForQuery), a BT page counts BT openings, analyst pages their
 * own type, and the all-roles "ABA cases" pages every type (null).
 * @param {string} roleKey seoLanding role key: rbt | bt | bcba | bcaba | aba-cases
 * @returns {string[]|null}
 */
export function seoCaseTypesForRole(roleKey) {
  if (roleKey === "rbt") return normalizeRoleTypeForQuery("rbt");
  if (roleKey === "bt") return ["BT"];
  if (roleKey === "bcaba") return [normalizeRoleTypeForQuery("bcaba")];
  if (roleKey === "bcba") return ["BCBA"];
  return null;
}

/**
 * @description Applies all active case filters to a Supabase query builder.
 *   Used as the fallback path when the search_cases_v2 RPC is unavailable.
 * @param {Object} query - Supabase query builder
 * @param {Object} options - Filter options
 * @param {string}   options.userRole
 * @param {string}   [options.filterType]
 * @param {Object|null} [options.latBounds] - {minLat, maxLat, minLng, maxLng}
 * @param {string}   [options.filterEmp]
 * @param {string}   [options.filterSetting]
 * @param {string}   [options.filterClientSex]
 * @param {string}   [options.filterMinHours]
 * @param {string[]} [options.filterSkills]   - Skills overlap filter (OR semantics)
 * @param {string[]} [options.filterPayers]   - Payer types overlap filter (OR semantics)
 * @param {string}   [options.filterAgeMin]   - Minimum client age
 * @param {string}   [options.filterAgeMax]   - Maximum client age
 * @param {string}   [options.filterMinPay]   - Minimum hourly pay
 * @param {string}   [options.filterSupervision] - BCaBA supervision arrangement
 * @param {string}   [options.filterUrgency]    - "any" | "high"
 * @param {string}   [options.filterFreshness]  - "any" | "7" | "30" | "90" days
 * @param {boolean}  [options.filterTrainingProvided] - Only cases with training
 * @param {boolean}  [options.filterBcbaSupport]      - Only cases with BCBA support
 * @param {boolean}  [options.filterMileage]           - Only cases with mileage reimbursement
 * @returns {Object} Modified Supabase query builder
 */
export function applyCaseQueryFilters(query, {
  userRole,
  filterType = "All",
  latBounds = null,
  filterEmp = "All",
  filterSetting = "All",
  filterClientSex = "All",
  filterMinHours = "Any",
  filterSkills = [],
  filterPayers = [],
  filterAgeMin = "",
  filterAgeMax = "",
  filterMinPay = "",
  filterSupervision = "all",
  filterUrgency = "any",
  filterFreshness = "any",
  filterTrainingProvided = false,
  filterBcbaSupport = false,
  filterMileage = false,
} = {}) {
  let q = query
    .eq("status", "active")
    .or("flagged.is.null,flagged.eq.false");

  const typeValues = Array.isArray(filterType) ? filterType : (filterType && filterType !== "All" ? [filterType] : []);
  const empValues = Array.isArray(filterEmp) ? filterEmp : (filterEmp && filterEmp !== "All" ? [filterEmp] : []);
  const settingValues = Array.isArray(filterSetting) ? filterSetting : (filterSetting && filterSetting !== "All" ? [filterSetting] : []);
  const clientSexValues = Array.isArray(filterClientSex) ? filterClientSex : (filterClientSex && filterClientSex !== "All" ? [filterClientSex] : []);
  const supervisionValues = Array.isArray(filterSupervision) ? filterSupervision : (filterSupervision && filterSupervision !== "all" ? [filterSupervision] : []);

  const resolvedType = normalizeRoleTypeForQuery(userRole, filterType);
  if (Array.isArray(resolvedType)) q = q.in("type", resolvedType);
  else if (resolvedType) q = q.eq("type", resolvedType);
  else if (typeValues.length > 1) q = q.in("type", typeValues);

  if (latBounds) {
    q = q
      .gte("lat", latBounds.minLat)
      .lte("lat", latBounds.maxLat)
      .gte("lng", latBounds.minLng)
      .lte("lng", latBounds.maxLng);
  }

  if (empValues.length === 1) {
    q = q.or(`employment.eq.${empValues[0]},employment.like.%${empValues[0]}%`);
  }

  if (settingValues.length === 1) {
    q = q.or(`setting.eq.${settingValues[0]},setting.like.%${settingValues[0]}%`);
  }

  if (clientSexValues.length === 1) {
    q = q.or(`client_sex.eq.Not Specified,client_sex.eq.${clientSexValues[0]}`);
  }

  if (filterMinHours && filterMinHours !== "Any") {
    const threshold = parseInt(filterMinHours, 10);
    if (!Number.isNaN(threshold)) q = q.gte("hours", threshold);
  }

  // Skills: case must share at least one skill with the filter list (OR / overlap).
  // Cases with empty skills[] (no required skills) are always included.
  if (filterSkills.length > 0) {
    q = q.or(`skills.is.null,skills.ov.{${filterSkills.map(s => `"${s}"`).join(",")}}`);
  }

  // Payer types: case must share at least one payer with the filter list (OR / overlap).
  // Cases with no payer types are always included.
  if (filterPayers.length > 0) {
    q = q.or(`payer_types.is.null,payer_types.ov.{${filterPayers.map(p => `"${p}"`).join(",")}}`);
  }

  // Age range: filter range must overlap the case's age range.
  // Cases with no age data are always included.
  if (filterAgeMin) {
    const min = parseInt(filterAgeMin, 10);
    if (!Number.isNaN(min)) q = q.or(`age_max.is.null,age_max.gte.${min}`);
  }
  if (filterAgeMax) {
    const max = parseInt(filterAgeMax, 10);
    if (!Number.isNaN(max)) q = q.or(`age_min.is.null,age_min.lte.${max}`);
  }

  // Min pay: hourly cases only; cases without pay_min set are always included.
  if (filterMinPay) {
    const threshold = parseInt(filterMinPay, 10);
    if (!Number.isNaN(threshold)) q = q.or(`pay_min.is.null,pay_min.gte.${threshold}`);
  }

  // BCaBA supervision arrangement
  if (supervisionValues.length === 1) {
    q = q.eq("bcaba_supervision", supervisionValues[0]);
  }

  // ── Tier-2 filters ────────────────────────────────────────────────────────

  // Urgency: "high" shows only urgent cases
  if (filterUrgency && filterUrgency !== "any") {
    q = q.eq("urgency", filterUrgency);
  }

  // Freshness: filter by confirmed_at (cases without confirmed_at treated as stale)
  if (filterFreshness && filterFreshness !== "any") {
    const days = parseInt(filterFreshness, 10);
    if (!Number.isNaN(days)) {
      const cutoff = new Date(Date.now() - days * 86400000).toISOString();
      q = q.gte("confirmed_at", cutoff);
    }
  }

  // Convenience perks: only include cases that explicitly offer each perk
  if (filterTrainingProvided) q = q.eq("training_provided", true);
  if (filterBcbaSupport)      q = q.eq("bcba_support", true);
  if (filterMileage)          q = q.eq("mileage_reimbursement", true);

  return q;
}

/**
 * @description Returns true if the Load More button should be shown in FindCasesV2.
 * @param {Object} params
 * @param {boolean} params.hasMoreFromServer - Whether the server has more results
 * @param {number} params.loadedCount - Number of cases loaded from server so far
 * @param {number} params.visibleCount - Number of cases currently visible in the list
 * @param {number} params.totalCount - Server-reported total result count
 * @returns {boolean}
 */
export function shouldShowLoadMore({
  hasMoreFromServer,
  loadedCount,
  visibleCount,
  totalCount,
}) {
  if (!hasMoreFromServer) return false;
  if (loadedCount < PAGE_SIZE) return false;
  if (visibleCount >= totalCount) return false;
  return true;
}
